import type { CompanyShopRepository } from "../../../domain/repositories/company/company-shop-repository";
import type {
  CreateBannerRequest,
  CreateShopSectionRequest,
  DeleteBannerRequest,
  ShopBannerEntity,
  ShopFooterDesign,
  ShopHeaderDesign,
  ShopManagementDto,
  ShopSectionEntity,
  UpdateBannerRequest,
  UpdateBestsellerRequest,
  UpdateShopFooterDesignRequest,
  UpdateShopHeaderDesignRequest,
  UpdateShopSectionOrderRequest,
  UpdateShopSectionRequest,
  UpdateShopSettingsRequest,
} from "../../../domain/types/company-shop";
import { ShopBannerEntitySchema } from "../../../domain/types/company-shop";
import { ShopManagementDtoSchema } from "../../../domain/types/company-shop";
import {
  ShopFooterDesignSchema,
  ShopHeaderDesignSchema,
  ShopSectionEntitySchema,
} from "../../../domain/types/company-shop";
import type { ProductEntity } from "../../../domain/types/product";
import { companyShopApiService } from "../../api/company/company-shop-api-service";
import { ProductEntitySchema } from "../../../domain/types/product";

export class CompanyShopRepositoryImpl implements CompanyShopRepository {
  async getShopHome(companyId: number): Promise<ShopManagementDto> {
    const response = await companyShopApiService.getShopHome(companyId);

    if (response.data) {
      return ShopManagementDtoSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "쇼핑몰 홈 가져오기 실패.");
  }

  async updateShopSettings(
    companyId: number,
    command: UpdateShopSettingsRequest,
  ): Promise<ShopManagementDto> {
    const response = await companyShopApiService.updateShopSettings(
      companyId,
      command,
    );

    if (response.data) {
      return ShopManagementDtoSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "쇼핑몰 설정 업데이트 실패.");
  }

  async updateBestsellers(
    companyId: number,
    command: UpdateBestsellerRequest,
  ): Promise<ShopManagementDto> {
    const response = await companyShopApiService.updateBestSellers(
      companyId,
      command,
    );

    if (response.data) {
      return ShopManagementDtoSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "베스트셀러 업데이트 실패.");
  }

  async getShopBanners(companyId: number): Promise<ShopBannerEntity[]> {
    const response = await companyShopApiService.getShopBanners(companyId);

    if (response.data) {
      return response.data.map((item) => ShopBannerEntitySchema.parse(item));
    }

    throw new Error(response.errorMsg || "베스트셀러 업데이트 실패.");
  }

  async createBanner(request: CreateBannerRequest): Promise<ShopBannerEntity> {
    const response = await companyShopApiService.createShopBanners(request);

    if (response.data) {
      return ShopBannerEntitySchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "베스트셀러 업데이트 실패.");
  }

  async updateBanner(request: UpdateBannerRequest): Promise<ShopBannerEntity> {
    const response = await companyShopApiService.updateShopBanners(request);

    if (response.data) {
      return ShopBannerEntitySchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "베스트셀러 업데이트 실패.");
  }

  async deleteBanner(request: DeleteBannerRequest): Promise<void> {
    const response = await companyShopApiService.deleteBanner(request);

    if (response.data) {
      return;
    }

    throw new Error(response.errorMsg || "베스트셀러 업데이트 실패.");
  }

  async getShopProducts(companyId: number): Promise<ProductEntity[]> {
    const response = await companyShopApiService.getShopProducts(companyId);

    if (response.data) {
      return response.data.map((item) => ProductEntitySchema.parse(item));
    }

    throw new Error(response.errorMsg || "베스트셀러 업데이트 실패.");
  }

  async updateStripBanner(params: {
    companyId: number;
    bannerFile?: File;
    command: { enabled: boolean; link?: string | null };
  }): Promise<ShopManagementDto> {
    const response = await companyShopApiService.updateStripBanner(params);

    if (response.data) {
      return ShopManagementDtoSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "띠배너 업데이트 실패.");
  }

  async deleteStripBanner(companyId: number): Promise<ShopManagementDto> {
    const response = await companyShopApiService.deleteStripBanner(companyId);

    if (response.data) {
      return ShopManagementDtoSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "띠배너 삭제 실패.");
  }

  async getShopSections(companyId: number): Promise<ShopSectionEntity[]> {
    const response = await companyShopApiService.getShopSections(companyId);

    if (response.data) {
      return response.data.map((item) => ShopSectionEntitySchema.parse(item));
    }

    throw new Error(response.errorMsg || "섹션 목록 조회 실패.");
  }

  async createShopSection(
    companyId: number,
    request: CreateShopSectionRequest,
  ): Promise<ShopSectionEntity> {
    const response = await companyShopApiService.createShopSection(
      companyId,
      request,
    );

    if (response.data) {
      return ShopSectionEntitySchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "섹션 생성 실패.");
  }

  async updateShopSection(
    companyId: number,
    sectionId: number,
    request: UpdateShopSectionRequest,
  ): Promise<ShopSectionEntity> {
    const response = await companyShopApiService.updateShopSection(
      companyId,
      sectionId,
      request,
    );

    if (response.data) {
      return ShopSectionEntitySchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "섹션 수정 실패.");
  }

  async deleteShopSection(
    companyId: number,
    sectionId: number,
  ): Promise<void> {
    // 204 로 본문 없이 내려올 수 있으므로 data 유무로 성공을 판단하지 않는다.
    // 실패는 api-service 가 errorCode 를 보고 이미 throw 한다.
    await companyShopApiService.deleteShopSection(companyId, sectionId);
  }

  async updateShopSectionOrder(
    companyId: number,
    request: UpdateShopSectionOrderRequest,
  ): Promise<ShopSectionEntity[]> {
    const response = await companyShopApiService.updateShopSectionOrder(
      companyId,
      request,
    );

    if (response.data) {
      return response.data.map((item) => ShopSectionEntitySchema.parse(item));
    }

    throw new Error(response.errorMsg || "섹션 순서 저장 실패.");
  }

  async updateShopHeaderDesign(
    companyId: number,
    request: UpdateShopHeaderDesignRequest,
  ): Promise<ShopHeaderDesign> {
    const response = await companyShopApiService.updateShopHeaderDesign(
      companyId,
      request,
    );

    if (response.data) {
      return ShopHeaderDesignSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "헤더 디자인 저장 실패.");
  }

  async updateShopFooterDesign(
    companyId: number,
    request: UpdateShopFooterDesignRequest,
  ): Promise<ShopFooterDesign> {
    const response = await companyShopApiService.updateShopFooterDesign(
      companyId,
      request,
    );

    if (response.data) {
      return ShopFooterDesignSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "푸터 디자인 저장 실패.");
  }
}
